import React, { useEffect, useRef } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { translate } from 'src/locales/i18next/config';
import Translations from 'src/locales/i18next/translations/translation';
import useVendorData from 'src/services/hooks/useVendorData';
import { useAppDispatch, useAppSelector } from '../../../../services/redux/app/Store';
import { fetchUnseenSharedOfferCounts } from '../../../../services/redux/slices/promotion/vendor-shared-offers-slice';
import { checkNewCompletedTickets } from '../../../../services/redux/slices/ticket/completed-ticket-slice';
import { checkNewTickets } from '../../../../services/redux/slices/ticket/ticket-slice';
import { getEnotcaSSOUrl } from '../../../../services/ssoEnocta';
import { MenuInnerWithSub } from './MenuInnerWithSub';
import { MenuItem } from './MenuItem';
import { useNewFeaturePulse } from './useNewFeaturePulse';

export function MenuInner() {
  const t = translate(Translations);
  const { offerEnabled, creditUsageAllowed, user } = useAppSelector((state) => state.auth);
  const { _checkField } = useVendorData();

  const { data } = useAppSelector((state) => state.completedTicket);
  const { data: ticket } = useAppSelector((state) => state.ticketList);
  const { vendorSharedPromotions, vendorSharedCoupons } = useAppSelector((state) => state.vendorSharedOffers);
  const dispatch = useAppDispatch();
  const history = useHistory();

  const handleEnotcaClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const url = await getEnotcaSSOUrl();
    window.open(url, '_blank');
  };

  const lastFetchedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (user?.vendor?.id) {
      dispatch(fetchUnseenSharedOfferCounts());
    }
  }, [history.location.pathname, user?.vendor?.id, dispatch]);

  useEffect(() => {
    const currentPath = history.location.pathname;
    if (lastFetchedPathRef.current === currentPath) return;
    lastFetchedPathRef.current = currentPath;

    dispatch(checkNewCompletedTickets());
    dispatch(checkNewTickets());
  }, [history.location.pathname, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(checkNewCompletedTickets());
      dispatch(checkNewTickets());
    }, 180000);

    return () => {
      clearInterval(interval);
    };
  }, [dispatch]);

  const showFlashSale = _checkField('flashSaleVisible', true) || _checkField('flashSaleProduct', true);
  const showGroupSales = _checkField('isGroupSales', true);
  const showBestSellingProducts = _checkField('biSuggestProductExist', true);
  const showInstallmentManagement = _checkField('installmentPolicyPermission', 'allowed');

  const campaignUnseenCount = vendorSharedPromotions?.count || 0;
  const couponUnseenCount = vendorSharedCoupons?.count || 0;
  const totalUnseenCount = campaignUnseenCount + couponUnseenCount;
  const discountRightBadge = totalUnseenCount > 0 ? { title: 'Yeni', variant: 'primary' as const, className: 'fs-10px p-1' } : undefined;

  // Pulse, kullanıcı tüm yeni özellik sayfalarını ziyaret edene kadar görünür.
  // Yeni bir sayfa tanıtmak için listeye bir satır eklemek yeterli.
  const discountPulseVisible = useNewFeaturePulse([
    { path: '/flash-products', enabled: showFlashSale },
    { path: '/group-buy-offers', enabled: showGroupSales },
  ]);

  return (
    <>
      <MenuItem to="/dashboard" icon="/media/logos/logo-idefix.svg" logo />
      <div className="mx-5"></div>

      <MenuInnerWithSub title="Ürünlerim" to="/products" menuPlacement="bottom-start" menuTrigger="{default:'click', lg: 'hover'}">
        {/* PAGES */}
        <MenuItem to="/products/my-products/matching-products" title="Tümü" />
        <MenuItem to="/products/product-operations" title="Ürün İşlemleri" />
        <MenuItem to="/products/questions" title="Ürün Soruları" />
        <MenuItem to="/products/evaluation" title="Ürün Değerlendirmeleri" />
        <MenuItem to="/products/add-products/bulk-options" title="Marka Ekleme Talepleri" />
      </MenuInnerWithSub>

      <MenuItem title="Envanter" to="/inventory" />
      {/* PAGES */}

      <MenuInnerWithSub title="Sipariş" to="/orders" menuPlacement="bottom-start" menuTrigger="{default:'click', lg: 'hover'}">
        {/* PAGES */}
        <MenuItem to="/orders/orders-management" title="Siparişler" />
        <MenuItem to="/orders/cancelled" title="İptaller" />
        <MenuItem to="/orders/refund" title="İadeler" />
        <MenuItem to="/orders/change-requests" title="Değişim Talepleri" />
        <MenuItem to="/orders/cargo" title="Kargo İşlemleri" />
        <MenuItem to="/orders/questions" title="Sipariş Soruları" />
        <MenuItem to="/orders/order-evaluation" title="Sipariş Değerlendirmeleri" />
      </MenuInnerWithSub>

      <MenuInnerWithSub title="Finans" to="/finance" menuPlacement="bottom-start" menuTrigger="{default:'click', lg: 'hover'}">
        {/* PAGES */}
        <MenuItem to="/finance/vesting" title="Hak Ediş" />
        <MenuItem to="/finance/future-estimated-payments" title="Gelecek Tahmini Ödemeler" />
        {creditUsageAllowed && <MenuItem to="/finance/early-payment-request" title="Erken Ödeme Talebi" />}
        <MenuItem to="/finance/payment" title="Ödemeler" />
        <MenuItem to="/finance/invoices" title="Fatura İşlemleri" />
        <MenuItem to="/finance/invoice-requests" title="Fatura Talepleri" />
        <MenuItem to="/finance/current" title="Cari Hesap Ekstresi" />
        <MenuItem to="/finance/vendor-reconciliations" title="Dönem Mutabakatlarım" />
        <MenuItem to="/finance/stopaj" title="Stopaj" />
      </MenuInnerWithSub>

      <MenuItem to="/sponsored-products" title="Sponsorlu Ürünler" />

      <MenuInnerWithSub
        title="İndirim"
        showPulse={discountPulseVisible && totalUnseenCount === 0}
        to="/campaigns"
        menuPlacement="bottom-start"
        menuTrigger="{default:'click', lg: 'hover'}"
        rightBadge={discountRightBadge}
      >
        {/* PAGES */}
        <MenuItem to="/campaigns/list" title="İndirimler" />

        {showFlashSale ? <MenuItem to="/flash-products" title="Flaş Ürünler" rightBadge={{ title: 'Yeni', variant: 'danger', className: 'fs-10px p-1' }} /> : null}
        <MenuItem to="/campaigns/coupons" title="Kuponlar" />
        {offerEnabled && (
          <>
            <MenuItem to="/campaigns/discounts/pricing-page" title="Fiyatlandırma Sayfası" />
            <MenuItem to="/campaigns/discounts/commission-offers" title="Komisyon Teklifleri" />
            <MenuItem to="/campaigns/discounts/badge-offers" title="Etiket Teklifleri" />
          </>
        )}
        <MenuItem
          to="/campaigns/campaign-offers"
          title="Kampanyalar"
          rightBadge={campaignUnseenCount > 0 ? { title: vendorSharedPromotions.display, variant: 'primary', className: 'fs-10px p-1 rounded-pill' } : undefined}
        />
        <MenuItem
          to="/campaigns/coupon-offers"
          title="Kupon Tekliflerim"
          rightBadge={couponUnseenCount > 0 ? { title: vendorSharedCoupons.display, variant: 'primary', className: 'fs-10px p-1 rounded-pill' } : undefined}
        />
        {showGroupSales ? (
          <MenuItem to="/group-buy-offers" title={t(`pages.groupBuyOffers.offers.title`)} rightBadge={{ title: 'Yeni', variant: 'danger', className: 'fs-10px p-1' }} />
        ) : null}
        {showInstallmentManagement && (
          <>
            <MenuItem to="/products/installment-setup" title="Esnek Taksit Modeli" rightBadge={{ title: 'Yeni', variant: 'danger', className: 'fs-10px p-1' }} />
            <MenuItem to="/products/assigned-installment-policies" title="Atanmış Taksit Kurguları" rightBadge={{ title: 'Yeni', variant: 'danger', className: 'fs-10px p-1' }} />
          </>
        )}
      </MenuInnerWithSub>

      <MenuInnerWithSub title="Raporlar" to="/reports" menuPlacement="bottom-start" menuTrigger="{default:'click', lg: 'hover'}">
        {/* PAGES */}
        <MenuItem to="/reports/sales" title="Satış Raporları" />
        <MenuItem to="/reports/refund" title="İade Raporları" />
        <MenuItem to="/reports/cancel" title="İptal Raporları" />
        <MenuItem to="/reports/delayed" title="Geciken Sipariş Raporları" />
        <MenuItem to="/reports/sponsored-product" title="Sponsorlu Ürün Raporu" />
      </MenuInnerWithSub>

      <MenuItem
        to="/support"
        title={
          <span>
            Destek Talepleri
            {data?.totalCount || ticket?.totalCount ? (
              <Link to="/support/onlyUnRead">
                <span className="text-danger"> (Yeni Mesajınız Var)</span>
              </Link>
            ) : null}
          </span>
        }
      />
      <MenuItem to="/showcase" title="Vitrin" rightBadge={{ title: 'Yeni', variant: 'warning', className: 'fs-10px p-1' }} />

      {showBestSellingProducts ? (
        <MenuItem to="/best-selling-products" title="Tıkla Sat" rightImage={{ src: '/media/sidebar/click-to-sell-cursor.svg', className: 'h-45px' }} />
      ) : null}

      <div className="menu-item me-lg-1">
        <a href="#" onClick={handleEnotcaClick} className="menu-link py-3 fs-5 fw-boldest" rel="noreferrer">
          <span className="menu-title" style={{ color: '#006BDC' }}>
            idefix Bilgi Merkezi
          </span>
        </a>
      </div>
    </>
  );
}
